import React, { useState, useEffect, useMemo } from "react";
import Tab from "@mui/material/Tab";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import styled from "styled-components";
import _ from "lodash";

import theme from "../../styles/theme";

import PodiumBackground from "../Backgrounds//PodiumBackground";
import { default as lastRaceImage } from "../../assets/last-race.jpg";

import { LastRaceResulstTab, LastRaceResulstTabSceleton } from "./LastRaceResults";

import "swiper/css";
import "swiper/css/navigation";

import { SwitcherContainer, SwitcherBadge } from "./BackgroundPodiumDriverSlider";
import { getDivisionColor } from "../../utils/helpers/divisionUtils";

import BackgroundPodiumDriver from "./BackgroundPodiumDriverSlider";
import BackgroundPodiumTeam from "./BackgroundPodiumTeamSlider";

import { useScheduleContext } from "../../contexts/schedule";
import { useStandingsContext } from "../../contexts/standings";
import { isTeamsEmpty } from "../../utils/helpers/standings";

const StyledTab = styled(Tab)`
  color: ${theme.colors.white} !important;
  font-family: "Roboto Condensed" !important;

  &.Mui-selected {
    color: ${theme.colors.green} !important;
  }
  &.Mui-disabled {
    color: ${theme.colors.grey} !important;
  }
`;

const SeasonStandingsContainer = styled.div`
  max-width: 1300px;
  margin-top: 48px;
  margin-left: auto;
  margin-right: auto;
`;

const StyledTabList = styled(TabList)`
  width: 100vw;
  margin-left: calc(50% - 50vw);
  background-color: ${theme.colors.darkLight};
`;

const StyledTabPanel = styled(TabPanel)`
  padding: 0px !important;
`;

const Standings = () => {
  const [value, setValue] = useState(0);
  const { seasons, currentSeasonId, standingsSeasonId, loadSchedule } = useScheduleContext();
  const { standings } = useStandingsContext();

  const handleChange = (_, newValue) => {
    setValue(newValue);
  };

  const [currentStandingsSeasonId, setCurrentStandingsSeasonId] = useState(null);

  const teams = standings[currentStandingsSeasonId]?.teams;
  const teamsDisabled = isTeamsEmpty(teams);

  useEffect(() => {
    if (teamsDisabled && value === 1) {
      setValue(0);
    }
  }, [teamsDisabled, value]);

  // Standings open on the last season that actually raced, not on the current one: a season is
  // current from the moment it is created, long before anyone has scored in it. The backend names
  // that season in the standings-season rel; falling back to current keeps the old behaviour when
  // no season has scored at all.
  const initialStandingsSeasonId = standingsSeasonId ?? currentSeasonId;

  useEffect(() => {
    if (initialStandingsSeasonId && !currentStandingsSeasonId) {
      setCurrentStandingsSeasonId(initialStandingsSeasonId);
    }
  }, [initialStandingsSeasonId, currentStandingsSeasonId]);

  const season = useMemo(() => {
    return currentStandingsSeasonId ? seasons[currentStandingsSeasonId] : null;
  }, [seasons, currentStandingsSeasonId]);

  const [selectedChildSeasonIdForLastRace, setSelectedChildSeasonIdForLastRace] = useState(null);

  useEffect(() => {
    if (season?.childSeasons?.length > 0) {
      if (!selectedChildSeasonIdForLastRace || !season.childSeasons.find(c => c.id === selectedChildSeasonIdForLastRace)) {
        setSelectedChildSeasonIdForLastRace(season.childSeasons[0].id);
      }
    } else {
      setSelectedChildSeasonIdForLastRace(null);
    }
  }, [season, selectedChildSeasonIdForLastRace]);

  useEffect(() => {
    if (selectedChildSeasonIdForLastRace) {
      loadSchedule(selectedChildSeasonIdForLastRace);
    }
  }, [selectedChildSeasonIdForLastRace, loadSchedule]);

  const lastRaceSeason = useMemo(() => {
    if (!season) return null;
    return selectedChildSeasonIdForLastRace 
      ? (seasons[selectedChildSeasonIdForLastRace] || season.childSeasons.find(c => c.id === selectedChildSeasonIdForLastRace))
      : season;
  }, [season, seasons, selectedChildSeasonIdForLastRace]);

  if (!season) return <></>;

  const racesToSearch = lastRaceSeason?.races || [];
  const lastFinishedRaceIndex = _.findLastIndex(racesToSearch, (race) => race.hasResults);
  const lastFinishedRace = racesToSearch[lastFinishedRaceIndex];

  const switcherElement = season?.childSeasons?.length > 0 ? (
    <SwitcherContainer style={{ marginTop: 24, marginBottom: 24, position: "relative", zIndex: 2 }}>
      {season.childSeasons.map((child) => {
        const childSeasonData = seasons[child.id] || child;
        const colors = childSeasonData.divisionColors || season.divisionColors || {};
        const divKeys = Object.keys(colors);
        const colorKey = divKeys.length > 0 ? divKeys[0] : null;
        const divisionColor = colorKey ? getDivisionColor(colors, colorKey) : theme.colors.green;

        const active = selectedChildSeasonIdForLastRace === child.id;
        return (
          <SwitcherBadge
            key={child.id}
            $active={active}
            $color={divisionColor}
            $darkBackground={true}
            onClick={() => setSelectedChildSeasonIdForLastRace(child.id)}
          >
            {childSeasonData.shortName}
          </SwitcherBadge>
        );
      })}
    </SwitcherContainer>
  ) : null;

  return (
    <SeasonStandingsContainer>
      <TabContext value={value}>
        <StyledTabList onChange={handleChange} indicatorColor="secondary" centered>
          <StyledTab label="Пілоти" />
          <StyledTab label="Команди" disabled={teamsDisabled} />
          <StyledTab
            label="Останній Етап"
            // Gated on the season standings opened on, not on the current one — those now differ
            // whenever a new season exists, which would otherwise disable this tab permanently.
            disabled={currentStandingsSeasonId !== initialStandingsSeasonId}
          />
        </StyledTabList>

        <StyledTabPanel value={0} style={{ maxWidth: 1100, margin: "auto" }}>
          <BackgroundPodiumDriver
            currentSeasonId={currentStandingsSeasonId}
            setCurrentSeasonId={setCurrentStandingsSeasonId}
          />
        </StyledTabPanel>

        <StyledTabPanel value={1} style={{ maxWidth: 1100, margin: "auto" }}>
          <BackgroundPodiumTeam
            currentSeasonId={currentStandingsSeasonId}
            setCurrentSeasonId={setCurrentStandingsSeasonId}
          />
        </StyledTabPanel>

        <StyledTabPanel value={2} style={{ maxWidth: 1300, margin: "auto" }}>
          <PodiumBackground image={lastRaceImage} />
          {!lastFinishedRace && <LastRaceResulstTabSceleton switcherElement={switcherElement} />}
          {lastFinishedRace && (
            <LastRaceResulstTab 
              raceId={lastFinishedRace.id} 
              stage={lastFinishedRaceIndex + 1} 
              switcherElement={switcherElement}
            />
          )}
        </StyledTabPanel>
      </TabContext>
    </SeasonStandingsContainer>
  );
};

export default Standings;
